import type { OrderFeeLine } from '@modules/orders'

// INVESTMENT SUMMARY fee rows (Figma 32590:66206): DLD, Title Deed, NOC and
// Acquisition each map to their own backend fee (matched on `code`, never on
// the label). Any other non-zero line the quote returns (Platform,
// Tokenisation, …) follows as its own row, so the rows always add up to
// TOTAL PAYABLE minus the investment value.

interface NamedFee {
  /** Accepted codes — the first is the stable row key. */
  codes: readonly string[]
  /** Shown when the quote carries no line for this fee. */
  fallbackLabel: string
}

export const ACQUISITION_FEE_CODE = 'ACQUISITION_FEE'

/** Rows always shown, in Figma order. */
const NAMED_FEES: readonly NamedFee[] = [
  { codes: ['DLD_FEE'], fallbackLabel: 'DLD Fee' },
  { codes: ['TITLE_DEED_FEE', 'TITLE_DEED'], fallbackLabel: 'Title Deed Fee' },
  { codes: ['NOC_DOCUMENTATION_FEE'], fallbackLabel: 'NOC & Documentation Fee' },
  { codes: [ACQUISITION_FEE_CODE], fallbackLabel: 'Acquisition Fee' },
]

// Half a fil — anything closer is the same amount after rounding.
const ROUNDING_TOLERANCE = 0.005
const RATE_DECIMALS = 2

/** "2.0000%" / "2.00" → "2%"; unreadable rates are dropped. */
const normaliseRate = (rate: string | null): string | null => {
  if (!rate) return null
  const numeric = Number(rate.replace('%', '').trim())
  return Number.isFinite(numeric) ? `${parseFloat(numeric.toFixed(RATE_DECIMALS))}%` : null
}

export interface SummaryFeeRow {
  key: string
  /** "DLD Fee (2%)" — the rate in brackets when the BE quotes one. */
  label: string
  /** Null when the quote has no line for this fee — the row reads "-". */
  amount: number | null
  /** The BE line behind the row, for its detail panel. Null when absent. */
  line: OrderFeeLine | null
}

/**
 * The BE sends `vatAmount` beside `amount`. When the lines already add up to
 * the quoted total, `amount` is VAT-inclusive; otherwise VAT is added on top.
 * Same rule as InvestFeeBreakdown, so the summary and its panel agree.
 */
const isVatIncluded = (fees: OrderFeeLine[], totalFeeAmount: number): boolean => {
  const sum = fees.reduce((total, fee) => total + fee.amount, 0)
  return Math.abs(sum - totalFeeAmount) < ROUNDING_TOLERANCE
}

export const splitSummaryFees = (fees: OrderFeeLine[], totalFeeAmount: number): SummaryFeeRow[] => {
  const vatIncluded = isVatIncluded(fees, totalFeeAmount)
  const toRow = (line: OrderFeeLine): SummaryFeeRow => {
    const rate = normaliseRate(line.rate)
    return {
      key: line.code,
      label: rate ? `${line.label} (${rate})` : line.label,
      amount: vatIncluded ? line.amount : line.amount + line.vatAmount,
      line,
    }
  }

  const named = NAMED_FEES.map(({ codes, fallbackLabel }): SummaryFeeRow => {
    const line = fees.find((fee) => codes.includes(fee.code))
    return line
      ? toRow(line)
      : { key: codes[0] ?? fallbackLabel, label: fallbackLabel, amount: null, line: null }
  })

  const namedCodes = new Set(NAMED_FEES.flatMap(({ codes }) => codes))
  const others = fees
    .filter((fee) => !namedCodes.has(fee.code))
    .map(toRow)
    .filter((row) => Math.abs(row.amount ?? 0) >= ROUNDING_TOLERANCE)

  return [...named, ...others]
}
